import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/common/states';
import { Brand } from '@/components/layout/Brand';
import { useAuthStore } from '@/lib/auth/store';
import { AdminApiError } from '@/lib/api/client';

const schema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});
type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const status = useAuthStore((s) => s.status);
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();
  const location = useLocation();
  const [serverError, setServerError] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />;
  }

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      await login(values.email, values.password);
      navigate('/', { replace: true });
    } catch (err) {
      // The backend remaps bad credentials to code:4 'tokenExpired' — relabel it.
      if (err instanceof AdminApiError && err.isAuth) {
        setServerError('Invalid email or password.');
      } else {
        setServerError(err instanceof Error ? err.message : 'Sign in failed. Please try again.');
      }
    }
  };

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-2">
      {/* Brand panel */}
      <div
        className="relative hidden flex-col justify-between p-10 text-white lg:flex"
        style={{ background: 'linear-gradient(150deg, #1E2637 0%, #2b1220 55%, #f95959 140%)' }}
      >
        <Brand />
        <div className="max-w-md space-y-4">
          <h2 className="text-3xl font-semibold leading-tight">
            The control center for the PakGames platform.
          </h2>
          <p className="text-white/70">
            Members, finance queues, live game operations, content and compliance — one secure,
            role-aware console.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-white/60">
          <ShieldCheck className="h-4 w-4" /> Encrypted session · Role-based access · Full audit trail
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-6">
          <div className="lg:hidden">
            <Brand />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
            <p className="mt-1 text-sm text-muted-foreground">Enter your admin credentials to continue.</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                placeholder="admin@pakgames.local"
                {...register('email')}
              />
              {errors.email ? <p className="text-xs text-destructive">{errors.email.message}</p> : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                {...register('password')}
              />
              {errors.password ? (
                <p className="text-xs text-destructive">{errors.password.message}</p>
              ) : null}
            </div>

            {serverError ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {serverError}
              </div>
            ) : null}

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? <Spinner /> : null}
              Sign in
            </Button>
          </form>

          <p className="text-center text-xs text-muted-foreground">
            Sessions expire after 24 hours. Contact a Super Admin for access.
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
